/* The shell: header, three-column body, phone navigation.
 *
 * The layout is the client's, from the wireframe and the prototype:
 *
 *   290px  |  1fr  |  300px      left sidebar · feed · Ads rail
 *
 * with their two breakpoints — the Ads rail goes below 1220px, the sidebar
 * below 900px, leaving a single feed column on a phone. Those numbers are
 * lifted from docs/christnetwork-prototype.html rather than re-guessed.
 *
 * The top nav is the client's seven destinations. Three are built; the rest
 * are rendered and inert rather than hidden, because they are in the design
 * the client signed off and their absence would misrepresent the product.
 */
import {
  BookOpen,
  Clock,
  Bell,
  CalendarCheck,
  Church,
  HeartHandshake,
  Shield,
  UsersRound,
  UserRound,
  Home,
  LogOut,
  MapPin,
  Users,
  Search,
  ShoppingBag,
  Sparkles,
} from 'lucide-react'
import { useEffect } from 'react'
import { NavLink, useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { enablePush, onPushInForeground, pushGranted } from '@/api/push'
import { Avatar } from '@/components/ui/avatar'
import { AdsRail } from '@/components/AdsRail'
import { LeftSidebar } from '@/components/LeftSidebar'
import { useMe } from '@/api/queries'
import { useAdminItems } from '@/components/adminMenu'
import { NotificationBell } from '@/features/notifications/NotificationBell'
import { tokens } from '@/api/client'
import { cn } from '@/lib/utils'
import { useTabTap } from '@/lib/navigation'
import { isEmbedded } from '@/lib/embedded'

/* The client's top navigation, in their order:
   Home | Gospel | Prayer | Quiz | History | Store | Church Map */
/* `minor` items are the ones not built yet. They stay in the design — the
   client signed the seven off — but they hide below xl rather than squeezing
   the built ones or forcing the bar to scroll. A scrolling nav bar is worse
   than a shorter one. */
const NAV = [
  { label: 'Home', icon: Home, to: '/', end: true },
  { label: 'Gospel', icon: BookOpen, minor: true },
  { label: 'Prayer', icon: HeartHandshake, to: '/prayer', end: false },
  { label: 'Quiz', icon: Sparkles, minor: true },
  { label: 'History', icon: Clock, minor: true },
  { label: 'Store', icon: ShoppingBag, minor: true },
  { label: 'Church Map', icon: MapPin, minor: true },
]

/* The ones that exist today get a bottom bar on phones. Seven inert icons down
   there would be a row of dead ends. */
const PHONE_NAV = NAV.filter((n) => n.to)

/* Inside the phone app the page is all there is — see lib/embedded. Decided
   once per load, so the two shells never swap under a mounted page. */
export function AppShell({ children }: { children: React.ReactNode }) {
  if (isEmbedded()) {
    return <main className="min-h-screen bg-canvas p-s3">{children}</main>
  }
  return <FullShell>{children}</FullShell>
}

function FullShell({ children }: { children: React.ReactNode }) {
  const { data: me } = useMe()
  /* The sidebar is hidden below 900px, so on a phone the account menu is
     the way to Services and to the Administration menu. */
  const administers = useAdminItems().length > 0
  const navigate = useNavigate()
  const qc = useQueryClient()

  /* Browser notifications, for the person who has already said yes.
   *
   * NEVER ASKED FOR HERE. A permission prompt that appears because a page
   * loaded is the one people deny on reflex, and a denial is close to
   * permanent — the browser stops asking. Asking belongs to a deliberate
   * action; this only re-registers a browser that has already agreed, because
   * FCM rotates tokens and a rotated one that was never sent up is a browser
   * that silently stops receiving.
   *
   * Foreground messages refresh the bell: the service worker draws nothing
   * while the tab is in front of somebody, so this is the one moment the app
   * KNOWS something landed rather than waiting for the next poll. */
  useEffect(() => {
    if (!me) return
    if (pushGranted()) void enablePush()
    return onPushInForeground(() => {
      qc.invalidateQueries({ queryKey: ['notifications'] })
    })
  }, [me, qc])

  /* A tab takes you to its start — from the top, with what is new. See
     lib/navigation for why that is a reset rather than a refetch. */
  const onNavClick = useTabTap()

  function signOut() {
    tokens.clear()
    window.location.replace('/')
  }

  return (
    <div className="min-h-screen">
      <header className="sticky top-0 z-40 border-b border-line-2 bg-surface shadow-1">
        {/* min-w-0 on the flex children below is what stops this row forcing
            the page wider than the viewport. Without it a flex item refuses to
            shrink past its content and the whole page scrolls sideways. */}
        <div className="flex h-14 items-stretch gap-s3 px-s3 min-[900px]:px-s4">
          {/* ------------------------------------------------ brand + search */}
          <button
            onClick={() => {
              onNavClick('/')
              navigate('/')
            }}
            className="flex shrink-0 items-center gap-s2 self-center"
            aria-label="Connect In God — home"
          >
            <span className="grid h-9 w-9 place-items-center rounded-pill bg-brand">
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none"
                   stroke="#fff" strokeWidth="2.4" strokeLinecap="round">
                <path d="M12 4v16M6.5 9.5h11" />
              </svg>
            </span>
            <span className="hidden font-extrabold tracking-tight text-brand lg:block">
              Connect In God
            </span>
          </button>

          {/* Present because it is in the client's header. Disabled rather than
              faked: a box that accepts text and returns nothing is worse than
              one that says it is not ready. */}
          <div className="relative hidden w-[180px] min-w-0 shrink self-center lg:block lg:w-[220px]">
            <Search
              className="pointer-events-none absolute left-s3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-3"
              aria-hidden
            />
            <input
              type="search"
              placeholder="Search"
              aria-label="Search"
              disabled
              title="Search is not available yet"
              className="h-9 w-full cursor-not-allowed rounded-pill bg-surface-2 pl-9 pr-s3 text-base text-ink placeholder:text-ink-3"
            />
          </div>

          {/* ---------------------------------------------------- top nav */}
          {/* Tabs run the FULL height of the header so the active underline is
              flush with its bottom edge, the way Facebook's is. A shorter tab
              leaves the underline floating in the middle of the bar, which is
              what it was doing.

              `self-stretch` rather than a fixed height: the tab then tracks the
              header if its height ever changes. */}
          <nav className="mx-auto hidden h-full items-stretch md:flex">
            {NAV.map(({ label, icon: Icon, to, end }) =>
              to ? (
                <NavLink
                  key={label}
                  to={to}
                  end={end}
                  title={label}
                  onClick={() => onNavClick(to)}
                  className={({ isActive }) =>
                    cn(
                      'relative flex w-[52px] items-center justify-center transition-colors lg:w-[76px] xl:w-[96px]',
                      isActive
                        ? 'text-brand'
                        : 'text-ink-2 hover:bg-hover',
                    )
                  }
                  aria-label={label}
                >
                  {({ isActive }) => (
                    <>
                      <Icon
                        className={cn('h-7 w-7', isActive && 'fill-brand/10')}
                        aria-hidden
                      />
                      {/* Absolutely placed rather than a border, so it sits ON
                          the header's own bottom border instead of above it. */}
                      {isActive ? (
                        <span
                          className="absolute inset-x-0 -bottom-px h-[3px] bg-brand"
                          aria-hidden
                        />
                      ) : null}
                    </>
                  )}
                </NavLink>
              ) : (
                <span
                  key={label}
                  title={`${label} is not available yet`}
                  aria-label={`${label} — not available yet`}
                  className="hidden w-[52px] cursor-default items-center justify-center text-ink-3 xl:flex xl:w-[96px]"
                >
                  <Icon className="h-7 w-7" aria-hidden />
                </span>
              ),
            )}
          </nav>

          {/* ----------------------------------------------------- account */}
          <div className="ml-auto flex items-center gap-s2 self-center">
            <NotificationBell />

            {me ? (
              <DropdownMenu.Root>
                <DropdownMenu.Trigger asChild>
                  <button
                    className="flex items-center gap-s2 rounded-pill py-1 pl-1 pr-s2 transition-colors hover:bg-hover"
                    aria-label="Your account"
                  >
                    <Avatar name={me.first_name} src={me.avatar_url} size="sm" />
                    {/* The client's header carries the name, not just the
                        avatar. */}
                    <span className="hidden max-w-[9rem] truncate font-semibold lg:block">
                      {me.first_name}
                    </span>
                  </button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Portal>
                  <DropdownMenu.Content
                    align="end"
                    sideOffset={8}
                    className="z-50 min-w-[14rem] rounded-md bg-surface p-s2 shadow-3"
                  >
                    <div className="flex items-center gap-s3 px-s2 py-s2">
                      <Avatar name={me.first_name} src={me.avatar_url} />
                      <div className="min-w-0">
                        <div className="truncate font-semibold">
                          {me.first_name} {me.last_name ?? ''}
                        </div>
                        <div className="truncate text-sm text-ink-2">
                          {me.email ?? me.mobile}
                        </div>
                        {me.church_name ? (
                          <div className="truncate text-sm text-ink-2">
                            {me.church_name}
                          </div>
                        ) : null}
                      </div>
                    </div>

                    <DropdownMenu.Separator className="my-s2 h-px bg-line-2" />

                    {me.church_id ? (
                      <DropdownMenu.Item
                        onSelect={() => navigate('/services')}
                        className="flex cursor-pointer items-center gap-s2 rounded-sm px-s3 py-s2 text-base outline-none hover:bg-hover min-[900px]:hidden"
                      >
                        <CalendarCheck className="h-4 w-4" aria-hidden />
                        Services
                      </DropdownMenu.Item>
                    ) : null}
                    {administers ? (
                      <DropdownMenu.Item
                        onSelect={() => navigate('/administration')}
                        className="flex cursor-pointer items-center gap-s2 rounded-sm px-s3 py-s2 text-base outline-none hover:bg-hover min-[900px]:hidden"
                      >
                        <Shield className="h-4 w-4" aria-hidden />
                        Administration
                      </DropdownMenu.Item>
                    ) : null}

                    <DropdownMenu.Item
                      onSelect={() => navigate('/settings/profile')}
                      className="flex cursor-pointer items-center gap-s2 rounded-sm px-s3 py-s2 text-base outline-none hover:bg-hover"
                    >
                      <UserRound className="h-4 w-4" aria-hidden />
                      My profile
                    </DropdownMenu.Item>

                    {me.church_id ? (
                      <DropdownMenu.Item
                        onSelect={() => navigate('/settings/church')}
                        className="flex cursor-pointer items-center gap-s2 rounded-sm px-s3 py-s2 text-base outline-none hover:bg-hover"
                      >
                        <Church className="h-4 w-4" aria-hidden />
                        Church details
                      </DropdownMenu.Item>
                    ) : null}

                    <DropdownMenu.Item
                      onSelect={() => navigate('/settings/family')}
                      className="flex cursor-pointer items-center gap-s2 rounded-sm px-s3 py-s2 text-base outline-none hover:bg-hover"
                    >
                      <UsersRound className="h-4 w-4" aria-hidden />
                      My family
                    </DropdownMenu.Item>

                    <DropdownMenu.Item
                      onSelect={() => navigate('/settings/notifications')}
                      className="flex cursor-pointer items-center gap-s2 rounded-sm px-s3 py-s2 text-base outline-none hover:bg-hover"
                    >
                      <Bell className="h-4 w-4" aria-hidden />
                      Notifications
                    </DropdownMenu.Item>

                    <DropdownMenu.Item
                      onSelect={signOut}
                      className="flex cursor-pointer items-center gap-s2 rounded-sm px-s3 py-s2 text-base outline-none hover:bg-hover"
                    >
                      <LogOut className="h-4 w-4" aria-hidden />
                      Sign out
                    </DropdownMenu.Item>
                  </DropdownMenu.Content>
                </DropdownMenu.Portal>
              </DropdownMenu.Root>
            ) : null}
          </div>
        </div>
      </header>

      {/* ----------------------------------------------------- three columns */}
      {/* FULL WIDTH, not a centred container. The side columns pin to the
          viewport edges and the feed centres in what is left — the way
          Facebook lays it out. A max-width wrapper would float the whole thing
          in the middle of a wide screen with dead margins either side.

          The feed column keeps its own 590px cap because a line of text 900px
          wide is unreadable; that is a typography limit, not a layout one. */}
      <div
        className={cn(
          // items-start would shrink each column to its content, and a sticky
          // child cannot travel inside a parent that is exactly its own
          // height — the rails would scroll away with the feed. Default
          // stretch gives each wrapper the row's full height instead.
          'grid gap-s4 py-s4',
          'px-s3 min-[900px]:px-s4',
          'grid-cols-1',
          'min-[900px]:grid-cols-[250px_minmax(0,1fr)]',
          'min-[1100px]:grid-cols-[290px_minmax(0,1fr)]',
          'min-[1220px]:grid-cols-[290px_minmax(0,1fr)_300px]',
          // Above 1500px the side columns stop growing and the feed takes the
          // rest, rather than leaving a wide empty gutter either side of it.
          'min-[1500px]:grid-cols-[320px_minmax(0,1fr)_340px]',
          // Past 1800px the feed would get uncomfortably wide, so the whole
          // grid stops growing and centres — the one place a cap helps.
          'min-[1800px]:mx-auto min-[1800px]:max-w-[1800px]',
        )}
      >
        {/* h-full so the sticky rail inside has the whole row to travel in. */}
        <div className="hidden h-full min-[900px]:block">
          <LeftSidebar />
        </div>

        {/* No max-width. The grid already gives the feed a bounded column —
            290px and 300px go to the rails, the rest is the feed — so capping
            it again only re-created the empty gutter this was meant to remove.
            The card's own text still wraps at a readable measure because the
            body copy sits inside padding, not because the column is narrow. */}
        <main className="w-full min-w-0 pb-16 md:pb-0">
          {children}
        </main>

        <div className="hidden h-full min-[1220px]:block">
          <AdsRail />
        </div>
      </div>

      {/* Phone navigation, where a thumb reaches. Only the built screens. */}
      <nav className="fixed bottom-0 left-0 right-0 z-40 flex border-t border-line-2 bg-surface md:hidden">
        {PHONE_NAV.map(({ label, icon: Icon, to, end }) => (
          <NavLink
            key={label}
            to={to!}
            end={end}
            onClick={() => onNavClick(to!)}
            className={({ isActive }) =>
              cn(
                'flex flex-1 flex-col items-center gap-0.5 py-s2 text-xs font-semibold transition-colors',
                isActive ? 'text-brand' : 'text-ink-2',
              )
            }
          >
            <Icon className="h-6 w-6" aria-hidden />
            {label}
          </NavLink>
        ))}
        {/* Groups, for everyone — the phone rail has no room for a tab only
            some people can open, and the sidebar it mirrors shows it to all. */}
        <NavLink
          to="/groups"
          onClick={() => onNavClick('/groups')}
          className={({ isActive }) =>
            cn(
              'flex flex-1 flex-col items-center gap-0.5 py-s2 text-xs font-semibold transition-colors',
              isActive ? 'text-brand' : 'text-ink-2',
            )
          }
        >
          <Users className="h-6 w-6" aria-hidden />
          Groups
        </NavLink>
      </nav>
    </div>
  )
}
